// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import DataTablePresentationPropsDefault from "@insite/mobius/DataTable/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { DataTableCssOverrides } from "@stoneage/MobiusOverrides/DataTable/DataTable";
import { fontConfig } from "@stoneage/XDL/constants";
import { xBaseTheme } from "@stoneage/XDL/types";
import { color, xScreen } from "@stoneage/XDL/Utilities";
import { css } from "styled-components";

const stickyMode = () => css`
    ${xScreen("lte", "sm")} {
        position: sticky;
        left: 0;
        z-index: 2;
        background-clip: padding-box;
        background: inherit;
        > ${TypographyStyle} {
            display: block;
            max-width: 140px;
            text-overflow: ellipsis;
            overflow: hidden;
        }
    }
`;

export default {
    defaultProps: {
        ...DataTablePresentationPropsDefault,
        mergeCss: true,
        cssOverrides: {
            header: css`
                background: ${color("common.ui7")};
                padding: 8px;
                border-bottom: 0;
                height: initial;
                line-height: normal;
            `,
            head: css`
                background: ${color("common.ui7")};
                th:first-child {
                    ${stickyMode()}
                }
            `,
            cell: css`
                padding: 8px;
                border-bottom: 0;
                height: initial;
                line-height: normal;
                ${StyledButton} > ${TypographyStyle} {
                    font-size: ${fontConfig.smallSingleLine.size}px;
                    line-height: ${fontConfig.smallSingleLine.lineHeight};
                }

                // Cells are white-space: nowrap, so line-clamp never triggers; use a single-line ellipsis
                & > ${TypographyStyle}, & > ${StyledButton} > ${TypographyStyle} {
                    display: block;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                }

                &:first-child {
                    ${stickyMode()}
                }
            `,
            row: css`
                background: ${color("common.white")};
                border-bottom: 1px solid ${color("common.border")};
            `,
            evenRow: css`
                background: ${color("common.ui4")};
            `,
        } as DataTableCssOverrides,
        headerTypographyProps: {
            mergeCss: true,
            ...fontConfig.smallSingleLine,
            weight: fontConfig.weight.medium,
            color: "common.ui4",
            css: css`
                letter-spacing: 0;
                display: flex;
                margin-right: 12px;
            `,
        },
        cellTypographyProps: {
            mergeCss: true,
            ...fontConfig.smallSingleLine,
            weight: fontConfig.weight.regular,
            color: "text.main",
            css: css`
                letter-spacing: 0;
            `,
        },
        sortClickableProps: {
            css: css`
                width: 100%;
                height: 100%;
                &:hover {
                    ${TypographyStyle}, ${IconWrapper} {
                        color: ${color("primary")};
                    }
                }
            `,
        },
        sortIconSources: {
            sortable: "IconArrowDropdownSmall",
            ascending: "IconArrowSmallWhiteDown",
            descending: "IconArrowSmallWhiteUp",
            none: "Minus",
            other: "Activity",
        },
        sortIconProps: {
            size: 12,
            color: "common.ui4",
            css: css`
                margin-left: auto;
            `,
        },
        css: css`
            td[class^="DataTableCellBase"] {
                & > ${TypographyStyle}:empty:after {
                    content: "--";
                }
            }
        `,
    },
} as xBaseTheme["dataTable"];
